import { useEffect, useState } from "react";
import { Trophy, Flame, Plus, Award, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ChallengesPage() {
  const [challenges, setChallenges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [logAmount, setLogAmount] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [logTarget, setLogTarget] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/challenges"); setChallenges(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const open = async (c) => {
    setSelected(c);
    try { const { data } = await api.get(`/challenges/${c.challenge_id}/leaderboard`); setLeaderboard(data); } catch {}
  };

  const join = async (c) => {
    try { await api.post("/challenges/join", { challenge_id: c.challenge_id }); toast.success(`Joined ${c.name}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const logProgress = async () => {
    if (!logTarget || !logAmount) return;
    try {
      const { data } = await api.post("/challenges/log", { challenge_id: logTarget.challenge_id, amount: Number(logAmount) });
      if (data.completed) toast.success(`🏆 Challenge complete! +${logTarget.reward_points} points`, { duration: 8000 });
      else toast.success(`Logged ${logTarget.target_unit === "£" ? "£" : ""}${logAmount}${logTarget.target_unit !== "£" ? " " + logTarget.target_unit : ""}`);
      setLogOpen(false); setLogAmount(""); setLogTarget(null);
      load();
      if (selected?.challenge_id === logTarget.challenge_id) open(logTarget);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Challenges</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl"><Trophy className="mr-2 inline h-7 w-7 text-accent" /> Win tokens. Earn badges. Build wealth.</h1>

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {challenges.map((c) => (
          <div key={c.challenge_id} className="flex flex-col rounded-2xl border-2 bg-card p-6" style={{ borderColor: `${c.color}50` }} data-testid={`challenge-${c.challenge_id}`}>
            <div className="flex items-start justify-between">
              <div className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" style={{ background: `${c.color}20`, color: c.color }}>{c.kind}</div>
              {c.completed && <Award className="h-5 w-5 text-accent" />}
            </div>
            <h3 className="mt-3 font-display text-xl">{c.name}</h3>
            <p className="mt-1 text-xs italic text-muted-foreground">{c.tagline}</p>
            <p className="mt-3 text-sm">{c.description}</p>

            {c.joined && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-mono">{c.target_unit === "£" ? "£" : ""}{c.progress}{c.target_unit !== "£" ? " " + c.target_unit : ""} / {c.target_unit === "£" ? "£" : ""}{c.target_amount}{c.target_unit !== "£" ? " " + c.target_unit : ""}</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-border">
                  <div className="h-full transition-all" style={{ width: `${c.progress_pct}%`, background: c.color }} data-testid={`progress-${c.challenge_id}`} />
                </div>
              </div>
            )}

            <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Flame className="h-3 w-3 text-amber-500" /> {c.reward_points} pts + {c.reward_badge}</span>
              <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {c.participants}</span>
            </div>

            <div className="mt-auto flex flex-col gap-2 pt-5">
              {!c.joined ? (
                <Button onClick={() => join(c)} className="text-white" style={{ background: c.color }} data-testid={`join-${c.challenge_id}`}>
                  <Plus className="mr-1 h-4 w-4" /> Join challenge
                </Button>
              ) : c.completed ? (
                <Button disabled className="bg-accent text-accent-foreground">🏆 Completed</Button>
              ) : (
                <Button onClick={() => { setLogTarget(c); setLogOpen(true); }} className="text-white" style={{ background: c.color }} data-testid={`log-${c.challenge_id}`}>
                  Log progress
                </Button>
              )}
              <button onClick={() => open(c)} className="text-xs text-muted-foreground hover:text-foreground" data-testid={`lb-${c.challenge_id}`}>View leaderboard →</button>
            </div>
          </div>
        ))}
      </div>

      {/* Log progress dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent data-testid="log-dialog">
          <DialogHeader>
            <DialogTitle>Log progress</DialogTitle>
            <DialogDescription>{logTarget ? `${logTarget.name} — current: ${logTarget.target_unit === "£" ? "£" : ""}${logTarget.progress}${logTarget.target_unit !== "£" ? " " + logTarget.target_unit : ""}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{logTarget?.target_unit === "£" ? "Amount £" : `${logTarget?.target_unit || "Amount"}`}</Label>
              <Input type="number" step="0.01" min="0.01" value={logAmount} onChange={(e) => setLogAmount(e.target.value)} className="h-11" data-testid="log-amount" autoFocus />
            </div>
            <Button onClick={logProgress} disabled={!logAmount} className="w-full" data-testid="log-submit">Log it</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leaderboard dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="leaderboard-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.name} — Leaderboard</DialogTitle>
            <DialogDescription>Top {leaderboard.length} of {selected?.participants}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {leaderboard.length === 0 ? <p className="text-sm text-muted-foreground">No participants yet — be the first.</p> : leaderboard.map((r, i) => (
              <div key={r.entry_id} className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`lb-row-${i}`}>
                <span className="flex items-center gap-3"><span className="font-mono text-muted-foreground">{i + 1}</span> {r.user_name}</span>
                <span className="font-display">{selected?.target_unit === "£" ? "£" : ""}{r.progress}{selected?.target_unit !== "£" ? " " + selected?.target_unit : ""}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
